import _ from 'lodash';
import { CriteriaSet, Criterias } from '../../../types';
import { CriteriaType, Operator, SignalIssuesMetadata } from '../types';
import {
  CriteriaDataType,
  CriteriaDisplay,
  CriteriaDisplayWithValue,
  IssueFilterGroup,
  SubcriteriaDisplay,
} from './types';

export const getDefaultCriteriaDisplayValue = (
  criteriaDisplay: CriteriaDisplay,
) => {
  switch (criteriaDisplay.criteriaType) {
    case CriteriaType.IssueCategory:
      return criteriaDisplay.key;
    case CriteriaType.SpecificIssue:
      return criteriaDisplay.key;
    case CriteriaType.HealthScore:
      return 0;
  }
  return '';
};

export const getDefaultValueForSubCriteria = (subCriteriaType: string) => {
  switch (subCriteriaType) {
    case 'affectedHrs':
      return {
        selectedValue: { range: '_duration_last_1d', minutes: 0 },
        criteriaType: subCriteriaType,
        operator: '=',
      };
    case 'duration':
      return {
        selectedValue: 0,
        criteriaType: subCriteriaType,
        operator: '=',
      };
    default:
      return '';
  }
};

export const signalIssueToCriteriaDisplay = (
  specificIssue: string,
  subCriteria: SubcriteriaDisplay[],
  signalIssues: SignalIssuesMetadata,
): CriteriaDisplay | undefined => {
  const issue = signalIssues.metadata.specificIssue.allowedValues.find(
    (i) => i.key === specificIssue,
  );
  if (!issue) return undefined;

  const subItems = signalIssues.categoryMapping[specificIssue] || [];

  const mappedIssue: CriteriaDisplay = {
    key: issue.key,
    displayName: issue.display,
    criteriaType: CriteriaType.SpecificIssue,
    dataType: signalIssues.metadata.specificIssue
      .selectedValueDatatype as CriteriaDataType,
    allowedSubCriterias: subCriteria,
    allowedOperators: signalIssues.metadata.specificIssue.allowedOperators,
    subItems: subItems
      .map((subItem) =>
        signalIssueToCriteriaDisplay(subItem, subCriteria, signalIssues),
      )
      .filter((iss): iss is CriteriaDisplay => !!iss)
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
  };
  return mappedIssue;
};

export const signalIssuesToCriteriaDisplay = (
  signalIssues: SignalIssuesMetadata,
): CriteriaDisplay[] => {
  const allowedSubCriteriasSpecificIssue =
    signalIssues.metadata.specificIssue.allowedSubCriterias;

  const specificIssueSubCriteria: SubcriteriaDisplay[] = _.keys(
    allowedSubCriteriasSpecificIssue,
  ).map((key) => {
    const keyTyped = key as keyof typeof allowedSubCriteriasSpecificIssue;
    const subCriteria = allowedSubCriteriasSpecificIssue[keyTyped];
    return {
      key: keyTyped,
      displayName: subCriteria.displayName,
      required: subCriteria.required,
      dataType: subCriteria.selectedValueDatatype as CriteriaDataType,
      allowedOperators: subCriteria.allowedOperators,
      allowedValues: [], // ! revise,
    };
  });

  const issueCategories: CriteriaDisplay[] = signalIssues.metadata.issueCategory.allowedValues
    .map((category) => ({
      key: category.key,
      displayName: category.display,
      criteriaType: CriteriaType.IssueCategory,
      allowedOperators: signalIssues.metadata.issueCategory.allowedOperators,
      dataType: signalIssues.metadata.issueCategory
        .selectedValueDatatype as CriteriaDataType,
      subItems: signalIssues.categoryMapping[category.key]
        .map((issue) =>
          signalIssueToCriteriaDisplay(
            issue,
            specificIssueSubCriteria,
            signalIssues,
          ),
        )
        .filter((iss): iss is CriteriaDisplay => !!iss)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const healthScore: CriteriaDisplay = {
    key: 'healthScore',
    displayName: signalIssues.metadata.healthScore.displayName,
    criteriaType: CriteriaType.HealthScore,
    allowedOperators: signalIssues.metadata.healthScore.allowedOperators,
    dataType: signalIssues.metadata.healthScore
      .selectedValueDatatype as CriteriaDataType,
  };

  return [...issueCategories, healthScore];
};

export const flattenCriteriaDisplays = (
  criteriaDisplays: CriteriaDisplay[],
): CriteriaDisplay[] => {
  return criteriaDisplays.flatMap((criteria) => {
    if (criteria.subItems && Boolean(criteria.subItems.length)) {
      return [criteria, ...flattenCriteriaDisplays(criteria.subItems)];
    }
    return criteria;
  });
};

// just an intermediate type to make the parsing easier
// only used as a utility in these helper funcs
type BasicIssueGroup = {
  operator: Operator;
  items: string[];
  externalOperator: Operator | null;
};

/**
 * Check if the sequence is an old style sequence (non-grouped).
 * If it includes `),uuid)` it's an old sequence.
 * New sequences (grouped) will instead be `),AND(uuid))`.
 */
const isLegacySequence = (sequence: string) => {
  const legacyRegex = /\),[A-Za-z0-9,]+\)/;
  const trimmedSequence = sequence.trim().replace(/\s/g, '');
  return legacyRegex.test(trimmedSequence);
};

/**
 * Parse old monitor criteria sequences to new groups.
 */
const parseLegacySequenceToGroups = (sequence: string): BasicIssueGroup[] => {
  const criteriaIds = extractCriteriaIds(sequence);
  const operators = extractSortedOperators(sequence);

  const groups: BasicIssueGroup[] = [
    {
      operator: operators[0] || Operator.AND,
      externalOperator: operators[1] || Operator.AND,
      items: criteriaIds.slice(0, 2),
    },
  ];

  // 1 or 2 items = AND(id1) / AND(id1, id2)
  if (criteriaIds.length <= 2) {
    return groups;
  }

  // operator index shifts by 2 because we have 2 operators per group,
  // the first is the one between the items in the groups (internal)
  // the second is the one between the groups (external)
  // we receive these operators from extractSortedOperators in the correct order (reversed from the sequence)
  // AND    (   AND   (   OR  (     AND (...
  // external  internal  external  internal
  // [AND,      OR,       AND,    AND] <- from extractSortedOperators
  let operatorIndex = 2;
  for (let i = 2; i < criteriaIds.length; i++) {
    groups.push({
      items: [criteriaIds[i]],
      operator: (operators[operatorIndex] as Operator) || Operator.AND,
      externalOperator:
        (operators[operatorIndex + 1] as Operator) || Operator.AND,
    });
    operatorIndex += 2;
  }

  return groups;
};

/**
 * Parses a monitor's criteria sequence string to issue groups.
 * Returns the groups' items as strings **(criteria IDs)**, to be populated with
 * the returned criteria later in the `criteriaToIssueGroup` function.
 */
const parseSequenceToGroups = (sequence: string): BasicIssueGroup[] => {
  // get rid of whitespace
  const trimmedSequence = sequence.replace(/\s/g, '');

  if (isLegacySequence(trimmedSequence))
    return parseLegacySequenceToGroups(sequence);

  // matches AND(id,id) OR(id) etc.
  const groupRegex = /(AND|OR)\([A-Za-z0-9,]+\)/g;
  let groups: (BasicIssueGroup & {
    groupString: string;
  })[] = [];

  // first we just find all separate groups, so the ones that have items
  // OR(AND(1,2),AND(3,4)) => we want ["AND(1,2)", "AND(3,4)"]
  const allItemGroups: string[] = [];
  let regArray: RegExpExecArray | null = null;
  while ((regArray = groupRegex.exec(trimmedSequence)) !== null) {
    allItemGroups.push(regArray[0]);
  }

  // we extract the items from the groups and create the group object
  // "AND(1,2)" => { operator: AND, items: [1, 2] }
  allItemGroups.forEach((group) => {
    const itemIds = extractCriteriaIds(group);
    groups = groups.concat({
      operator: /AND/.test(group) ? Operator.AND : Operator.OR,
      externalOperator: null,
      items: itemIds,
      groupString: group, // "AND(1,2)", we need this to find the externalOperator
    });
  });

  const firstGroup = groups[0];
  if (!firstGroup) return [];

  // we split the string before the first group, since all externalOperators appear before the first group
  // this gives us ["OR(", ",AND(3,4))"], the firs item is all the externalOperators
  const stringBefore = trimmedSequence.split(firstGroup.groupString)[0];
  const operatorStack: Operator[] = [];

  // extract all operators from the string before the first group
  const operatorRegex = /(AND|OR)/g;
  while ((regArray = operatorRegex.exec(stringBefore)) !== null) {
    operatorStack.push(regArray[0] === 'AND' ? Operator.AND : Operator.OR);
  }

  groups.forEach((group) => {
    // pop from the stack since the externalOperators appear in reverse order in the sequence string,
    // so the last operator in the string is between the first and second group in the UI
    // (G1) OR (G2) AND (G3) becomes AND(OR(...
    group.externalOperator = /AND/.test(operatorStack.pop() || Operator.AND)
      ? Operator.AND
      : Operator.OR;
  });

  return groups;
};

/**
 * `"AND(OR(..."` -> `[OR, AND]`
 */
export const extractSortedOperators = (sequence: string) => {
  const regex = /(AND|OR)/g;
  const matches = [];
  let match;

  while ((match = regex.exec(sequence)) !== null) {
    matches.push(match[0]);
  }

  return matches
    .map((op) => (op === 'AND' ? Operator.AND : Operator.OR))
    .reverse();
};

export const extractCriteriaIds = (sequence: string) => {
  const regex = /[a-fA-F0-9]{16,}/gi;
  const matches = [];
  let match;

  while ((match = regex.exec(sequence)) !== null) {
    matches.push(match[0]);
  }

  return matches;
};

export const getCriteriaHashMap = (sequence: string, criterias: Criterias) => {
  const criteriaIds = extractCriteriaIds(sequence);
  let criteriaHashMap = criteriaIds.reduce(
    (acc, criteriaId) => ({
      ...acc,
      [criteriaId]: criterias[criteriaId],
    }),
    {},
  );

  return criteriaHashMap as Criterias;
};

export const criteriasToIssueGroups = (
  criteriaSet: CriteriaSet,
  signalIssues: SignalIssuesMetadata,
): IssueFilterGroup[] => {
  const sortedCriterias = getCriteriaHashMap(
    criteriaSet.sequence,
    criteriaSet.criterias,
  );
  const clauseTypes = Object.keys(sortedCriterias).map((key) => ({
    ...sortedCriterias[key],
    key: key,
  }));
  const allCriteriaDisplayCategories = signalIssuesToCriteriaDisplay(
    signalIssues,
  );
  const groups = parseSequenceToGroups(criteriaSet.sequence);

  const allCriteriaDisplayIssues = allCriteriaDisplayCategories
    .flatMap((cat) => cat.subItems || [])
    .filter((criteria) => !!criteria) as CriteriaDisplayWithValue[];

  const criteriaCategoryDisplays = clauseTypes
    .map((clause, i) => {
      const criteria = allCriteriaDisplayCategories.find(
        (c) => c.key === clause.criteriaType || c.key === clause.selectedValue,
      );
      if (!criteria) return undefined;

      return {
        ...criteria,
        selectedValue: clause.selectedValue,
        operator: clause.operator
          ? clause.operator
          : criteria.allowedOperators[0].key || '=',
        subCriterias: [],
        key: clause.key,
      };
    })
    .filter((criteria) => !!criteria) as CriteriaDisplayWithValue[];

  const criteriaIssueDisplays: CriteriaDisplayWithValue[] = clauseTypes
    .map((clause, i) => {
      const criteria = allCriteriaDisplayIssues.find(
        (c) =>
          c.key === clause.selectedValue ||
          c.subItems?.find((s) => s.key === clause.selectedValue),
      );
      if (!criteria) return undefined;

      return {
        ...criteria,
        selectedValue: clause.selectedValue,
        operator: clause.operator
          ? clause.operator
          : criteria.allowedOperators[0].key || '=',
        subCriterias: clause.subCriterias
          ? _.cloneDeep(clause.subCriterias)
          : undefined,
        selectedSubItem: clause.selectedValue,
        key: clause.key,
      };
    })
    .filter((criteria) => !!criteria) as CriteriaDisplayWithValue[];

  const allCriterias = [...criteriaCategoryDisplays, ...criteriaIssueDisplays];
  const groupsWithCriterias = groups.map((issueGroup) => {
    const items = issueGroup.items.map((item) => {
      const criteria = allCriterias.find((c) => c.key === item);
      return criteria!;
    });

    return {
      operator: issueGroup.operator as Operator,
      externalOperator: issueGroup.externalOperator || Operator.AND,
      id: _.uniqueId(),
      name: _.uniqueId(),
      items: items.map((item) => ({
        ...item,
        uid: _.uniqueId(),
      })),
    };
  });

  return groupsWithCriterias;
};
